import Input, {
  Type as InputType,
  Size as InputSize,
  Color as InputColor,
} from 'components/Common/Input';
import { statuses, resolutionCodes, auditCodes } from 'utils/audits/values';
import { AuditStatusOriginal } from 'enums/status/auditStatus';
import FormControlSelect, {
  Color as SelectColor,
} from 'components/Common/FormControlSelect';
import Textarea from 'components/Common/Textarea';
import msg from 'utils/commonMessages';

import dateParse from 'utils/common/dateParse';
import classes from './AuditForm.module.scss';

export const getInputFields = ({
  isEditing,
  audit,
}: {
  isEditing: boolean;
  audit: {
    status: keyof typeof AuditStatusOriginal;
    [key: string]: any;
  };
}) => [
  {
    component: Input,
    name: 'auditId',
    renderProps: {
      label: `Audit ID*`,
      value: audit.auditId,
      type: InputType.Text,
      readOnly: true,
      disabled: true,
      size: InputSize.S,
    },
  },
  {
    component: Input,
    name: 'issuer',
    renderProps: {
      label: `Issuer*`,
      value: audit.issuer,
      type: InputType.Text,
      readOnly: true,
      disabled: true,
      size: InputSize.S,
    },
  },
  {
    component: Input,
    name: 'transactionId',
    renderProps: {
      label: `Transaction ID*`,
      value: audit.transactionId,
      type: InputType.Text,
      readOnly: true,
      disabled: true,
      size: InputSize.S,
    },
  },
  {
    name: 'auditCode',
    defaultValue: audit.auditCode,
    label: `Audit Code*`,
    labelClassName: classes.labelWidth,
    options: auditCodes,
    type: InputType.Text,
    readOnly: !isEditing,
    disabled: !isEditing,
    color: isEditing ? SelectColor.Black : SelectColor.Gray,
    component: FormControlSelect,
  },
  {
    component: Input,
    name: 'merchantName',
    renderProps: {
      label: `Merchant Name*`,
      type: InputType.Text,
      readOnly: !isEditing,
      disabled: !isEditing,
      size: InputSize.S,
      color: isEditing ? InputColor.Black : InputColor.Gray,
    },
  },
  {
    component: Input,
    name: 'preferredContactEmail',
    renderProps: {
      label: `Preferred Contact Email`,
      type: InputType.Text,
      readOnly: !isEditing,
      disabled: !isEditing,
      size: InputSize.S,
      color: isEditing ? InputColor.Black : InputColor.Gray,
      subtext: (
        <>
          Provide an email address if you wish to receive audit updates via
          email. If left blank, you must subscribe to the{' '}
          <a
            href="https://docs.getkard.com/api/notifications/notification-webhook#payload.body.data.auditUpdate"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            auditUpdate notification
          </a>{' '}
          to receive audit updates via webhook. Subscribing to the notification
          is highly recommended.
        </>
      ),
    },
  },
  {
    component: Textarea,
    name: 'auditDescription',
    renderProps: {
      label: `Audit Description*`,
      labelClassName: classes.labelWidth,
      type: InputType.Text,
      readOnly: !isEditing,
      disabled: !isEditing,
      placeholder: msg.DescriptionSupportMessage,
      color: isEditing ? InputColor.Black : InputColor.Gray,
      preventNewLines: true,
    },
  },
  {
    component: FormControlSelect,
    name: 'status',
    defaultValue: AuditStatusOriginal[audit.status],
    label: 'Status',
    labelClassName: classes.labelWidth,
    disabled: !isEditing,
    readOnly: !isEditing,
    options: statuses,
    color: isEditing ? SelectColor.Black : SelectColor.Gray,
  },
  {
    component: FormControlSelect,
    name: 'resolutionCode',
    defaultValue: audit.resolutionCode,
    label: 'Resolution Code',
    labelClassName: classes.labelWidth,
    disabled: !isEditing,
    readOnly: !isEditing,
    options: resolutionCodes,
    color: isEditing ? SelectColor.Black : SelectColor.Gray,
  },
  {
    component: Input,
    name: 'resolutionDescription',
    renderProps: {
      label: 'Resolution Description',
      value: audit.resolutionDescription,
      type: InputType.Text,
      disabled: !isEditing,
      readOnly: !isEditing,
      size: InputSize.S,
      color: isEditing ? InputColor.Black : InputColor.Gray,
    },
  },
  {
    component: Input,
    name: 'lastModified',
    renderProps: {
      label: 'Last Modified',
      value: `${dateParse(audit.lastModified)}`,
      type: InputType.Text,
      readOnly: true,
      disabled: true,
      size: InputSize.S,
      color: isEditing ? InputColor.Black : InputColor.Gray,
    },
  },
];
